import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { mxn } from "@/lib/format";
import { toast } from "sonner";

// Mínimo del motivo: el mismo que exige el backend (`sellado_solicitud.MIN_MOTIVO`).
const MIN_MOTIVO = 10;

// Cancelación LÓGICA de un crédito, SOLO ANTES DEL DESEMBOLSO (decisión del PO, sep-2026).
// Visible solo para el administrador de la entidad (el backend repite el candado y responde
// 409 si el dinero ya salió). El dato se conserva 10 años (51ª LFPIORPI): el crédito no se
// borra, su saldo queda en cero y sale de cartera, cobranza, mora, facturación, contabilidad,
// del reporte al SIC y del perfil de efectivo de PLD. El folio NO se reutiliza.
export function CancelarCreditoButton({ credito, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin_entidad") return null;
  if (credito?.cancelado || credito?.estatus === "Cancelado") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-600"
        data-testid={`credito-cancelado-${credito.id}`}
        title={credito?.cancelado_motivo ? `Motivo: ${credito.cancelado_motivo}` : undefined}
      >
        <Ban className="h-3.5 w-3.5" /> Crédito cancelado
        {credito?.cancelado_motivo ? ` · ${credito.cancelado_motivo}` : ""}
      </span>
    );
  }
  // Después del desembolso esta vía queda cerrada: la cartera se ajusta por reestructura,
  // liquidación o castigo, no por cancelación. Sin botón muerto en pantalla.
  const desembolsado = !!credito?.fecha_disposicion
    || String(credito?.estatus_desembolso || "") === "desembolsado";
  if (desembolsado) return null;

  const cancelar = async () => {
    const m = motivo.trim();
    if (m.length < MIN_MOTIVO) {
      toast.error(`Escribe el motivo de la cancelación (mínimo ${MIN_MOTIVO} caracteres)`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/creditos/${credito.id}`, { params: { motivo: m } });
      toast.success(data?.solicitud_cancelada
        ? "Crédito y solicitud cancelados. Nada se borró: el expediente se conserva."
        : "Crédito cancelado. Saldo en cero y dato conservado 10 años.");
      setOpen(false);
      setMotivo("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar el crédito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipButton
        tooltip="Cancelar este crédito antes del desembolso (solo administrador; pide motivo y queda en bitácora)"
        data-testid={`cancelar-credito-btn-${credito.id}`}
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 border-red-200 px-2.5 text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => setOpen(true)}
      >
        <Ban className="h-4 w-4" /> Cancelar
      </TooltipButton>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="cancelar-credito-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar el crédito {credito?.numero_credito}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  La cancelación es <strong>lógica</strong>: el crédito se conserva como evidencia
                  (10 años, 51ª LFPIORPI) y queda en bitácora con tu nombre, la fecha y el motivo.
                </p>
                <p>Al confirmar, este crédito:</p>
                <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-slate-600">
                  <li>queda con <strong>saldo insoluto en cero</strong> (saldo actual {mxn(credito?.saldo_insoluto)});</li>
                  <li>sale de cartera, cobranza, mora diaria, facturación y del reporte al SIC;</li>
                  <li>conserva su tabla de proyección y su garantía, marcadas como canceladas;</li>
                  <li>deja de alimentar el perfil de efectivo y el análisis de fraccionamiento de PLD;</li>
                  <li>cancela con él la <strong>solicitud</strong> que lo originó (el expediente y los documentos del cliente se conservan para futuros créditos).</li>
                </ul>
                <p className="text-[13px] text-slate-500">
                  El folio <strong>{credito?.numero_credito}</strong> no se reutiliza. Esta vía solo
                  aplica <strong>antes del desembolso</strong>: una vez entregado el dinero, la
                  cartera se ajusta por reestructura, liquidación o castigo.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="mb-1 block text-xs">
              Motivo de la cancelación <span className="text-red-500">*</span>
            </Label>
            <Textarea
              data-testid="cancelar-credito-motivo"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. el cliente desistió de la operación y pidió cancelar el trámite"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              Obligatorio, mínimo {MIN_MOTIVO} caracteres. Quedará en la bitácora de la entidad.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="cancelar-credito-volver">Volver</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirmar-cancelar-credito"
              onClick={(e) => { e.preventDefault(); cancelar(); }}
              disabled={busy || motivo.trim().length < MIN_MOTIVO}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirmar cancelación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
